import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCreditCard, FiBookOpen, FiShoppingBag, FiCalendar, FiCheckCircle, FiSearch, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';

const PaymentHistoryContainer = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: clamp(0rem, 1vw, 0.75rem) clamp(0rem, 1.5vw, 1rem) 2rem;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 1440px) {
    width: min(100%, 1280px);
  }

  @media (max-width: 1024px) {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  @media (max-width: 640px) {
    padding: 0 0.25rem 1.5rem;
  }

  @media (max-width: 360px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
  margin-bottom: 1.25rem;

  > div:first-child {
    min-width: 0;
  }

  @media (max-width: 1024px) {
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.65rem, 4vw, 2.5rem);
  font-weight: 650;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 480px) {
    line-height: 1.12;
  }
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  color: var(--text-muted, #64748b);
  font-size: 0.95rem;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 0.875rem;
  margin-bottom: 1.25rem;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const SummaryCard = styled.div`
  position: relative;
  overflow: hidden;
  background: var(--background-primary, #fff);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.045);

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 14px;
  }
`;

const SummaryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted, #64748b);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  @media (max-width: 380px) {
    font-size: 0.6875rem;
    letter-spacing: 0.04em;
  }
`;

const SummaryValue = styled.div`
  margin-top: 0.5rem;
  color: var(--text-primary, #1a202c);
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;

  @media (max-width: 480px) { font-size: 1.35rem; }
`;

const SummaryHint = styled.div`
  margin-top: 0.25rem;
  color: var(--text-light, #718096);
  font-size: 0.8125rem;
  line-height: 1.35;
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;

  @media (max-width: 1024px) and (min-width: 769px) {
    max-width: 320px;
  }
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 12px;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;

const Filters = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 640px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    margin-left: -0.25rem;
    margin-right: -0.25rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    gap: 0.5rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
  }
`;

const FilterButton = styled.button`
  padding: 0.6rem 0.95rem;
  border: 1px solid ${props => props.active ? 'rgba(184, 149, 106, 0.35)' : 'rgba(15, 23, 42, 0.10)'};
  border-radius: 999px;
  background-color: ${props => props.active ? 'rgba(184, 149, 106, 0.12)' : 'var(--background-primary)'};
  color: ${props => props.active ? '#7a5a32' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-size: 0.875rem;
  font-weight: 600;
  min-height: 40px;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.active ? 'rgba(184, 149, 106, 0.16)' : 'var(--background-secondary)'};
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 560px) {
    gap: 0.875rem;
  }
`;

const HistoryCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.045);
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
  
  &:hover {
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.075);
    border-color: rgba(184, 149, 106, 0.22);
    transform: translateY(-1px);
  }

  @media (hover: none) {
    &:hover {
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.045);
      border-color: rgba(15, 23, 42, 0.08);
      transform: none;
    }
  }

  @media (max-width: 560px) {
    border-radius: 14px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: rgba(248, 247, 244, 0.58);

  @media (max-width: 900px) {
    padding: 1.125rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.875rem;
    padding: 1rem;
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;

  @media (max-width: 360px) {
    gap: 0.625rem;
  }
`;

const TypeIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.$type === 'course' ? 'rgba(107, 142, 127, 0.13)' : 'rgba(184, 149, 106, 0.14)'};
  border: 1px solid ${props => props.$type === 'course' ? 'rgba(107, 142, 127, 0.20)' : 'rgba(184, 149, 106, 0.22)'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$type === 'course' ? '#527366' : '#8f6b3f'};
  font-size: 1.25rem;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
`;

const TypeLabel = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  overflow-wrap: anywhere;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CardDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted, #64748b);
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  min-width: 0;
  overflow-wrap: anywhere;
  line-height: 1.35;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`;

const HeaderAmount = styled.div`
  text-align: right;
  flex-shrink: 0;

  @media (max-width: 560px) {
    text-align: left;
    width: 100%;
  }
`;

const AmountLabel = styled.div`
  color: var(--text-light, #718096);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardContent = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;

  @media (max-width: 900px) {
    padding: 1.125rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 560px) {
    padding: 1rem;
  }
`;

const ItemName = styled.h4`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ItemDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  margin: 0 0 0.5rem 0;
  line-height: 1.5;
  gap: 1rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  @media (max-width: 360px) {
    gap: 0.4rem;
  }
`;

const DescriptionText = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const PaymentDetails = styled.div`
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(15, 23, 42, 0.07);
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.72);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
  gap: 0.75rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (max-width: 560px) {
    margin-top: 0.5rem;
    padding: 0.75rem;
    grid-template-columns: 1fr;
    gap: 0.625rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  min-width: 0;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 700;
  color: var(--text-light, #718096);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.68rem;

  @media (max-width: 360px) {
    font-size: 0.64rem;
  }
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DiscountBadge = styled.span`
  color: var(--success-color);
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;

  svg { flex-shrink: 0; }
  
  ${props => props.status === 'completed' && `
    background: rgba(34, 197, 94, 0.1);
    color: var(--success-color);
    border: 1px solid rgba(34, 197, 94, 0.2);
  `}
  
  ${props => props.status === 'pending' && `
    background: rgba(245, 158, 11, 0.1);
    color: var(--warning-color);
    border: 1px solid rgba(245, 158, 11, 0.2);
  `}
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
`;

const AmountSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--success-color);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
    justify-content: flex-start;
  }
`;

const ReceiptLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(184, 149, 106, 0.24);
  background: rgba(184, 149, 106, 0.08);
  margin-top: 0;
  width: fit-content;
  max-width: 100%;

  &:hover {
    color: var(--primary-dark);
    text-decoration: none;
    background: rgba(184, 149, 106, 0.13);
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    min-height: 36px;
    justify-content: center;
  }

  @media (max-width: 360px) {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }

  @media (max-width: 360px) {
    padding: 1.5rem 0.75rem;
  }

  h3, p {
    overflow-wrap: anywhere;
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--background-secondary);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: var(--text-secondary);
  font-size: 2rem;
  
  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    width: 52px;
    height: 52px;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }

  @media (max-width: 360px) {
    padding: 1.5rem 0.75rem;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-color);
  border: 1px solid var(--error-color);
  border-radius: 12px;
  padding: 1rem;
  color: var(--text-inverse);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }

  overflow-wrap: anywhere;
`;

const InfoMessage = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 1rem;
  color: var(--info-color);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }

  overflow-wrap: anywhere;
`;

function PaymentHistoryPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    if (user?.id) {
      fetchPaymentHistory();
    }
  }, [user?.id]);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch both course enrollments and product purchases
      const [enrollmentsResponse, purchasesResponse] = await Promise.all([
        api.getUserEnrollments(),
        api.getUserPurchases()
      ]);

      // Filter date threshold - only show purchases after 09/16/2025
      const historyStartDate = new Date('2025-09-16');
      
      // Combine and filter by date
      const allHistory = [
        ...enrollmentsResponse.enrollments
          .filter(enrollment => {
            if (enrollment.payment_status === 'pending') {
              return false;
            }

            // Show all enrollments with successful payment transactions (paid courses)
            // For free courses, only show if after the start date
            if (enrollment.payment_status === 'succeeded' && enrollment.amount > 0) {
              return true; // Always show successful paid enrollments
            }
            // For free courses or enrollments without payment info, apply date filter
            const enrollmentDate = new Date(enrollment.enrolled_at);
            return enrollmentDate >= historyStartDate;
          })
          .map(enrollment => ({
            id: `enrollment-${enrollment.id}`,
            type: 'course',
            typeIcon: <FiBookOpen />,
            typeLabel: 'Course Enrollment',
            name: enrollment.course_name,
            description: `${enrollment.course_code} - ${enrollment.academic_year || '2025-2026'}`,
            amount: parseFloat(enrollment.amount) || 0,
            originalPrice: parseFloat(enrollment.original_price) || 0,
            date: enrollment.enrolled_at,
            purchaseDate: enrollment.enrolled_at,
            orderNumber: `CE-${String(enrollment.id).padStart(6, '0')}`,
            status: enrollment.payment_status === 'succeeded' ? 'completed' : 'completed',
            receiptUrl: enrollment.receipt_url,
            paymentIntentId: enrollment.payment_intent_id || null
          })),
        ...purchasesResponse.purchases
          .filter(purchase => purchase.payment_status !== 'pending' && new Date(purchase.purchase_date) >= historyStartDate)
          .map(purchase => ({
            id: `purchase-${purchase.id}`,
            type: 'product',
            typeIcon: <FiShoppingBag />,
            typeLabel: 'Product Purchase',
            name: purchase.product_name,
            description: purchase.product_type,
            amount: purchase.total_amount,
            date: purchase.purchase_date,
            purchaseDate: purchase.purchase_date,
            orderNumber: `PP-${String(purchase.id).padStart(6, '0')}`,
            status: purchase.payment_status === 'succeeded' ? 'completed' : purchase.payment_status,
            receiptUrl: purchase.receipt_url
          }))
      ];

      // Sort by date (newest first)
      allHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setHistory(allHistory);
    } catch (error) {
      logger.error('Error fetching payment history', { error });
      // If it's a 404, 500, or route not found, show the date restriction message instead of error
      if (error.message.includes('Route not found') || 
          error.message.includes('404') || 
          error.message.includes('500') ||
          error.message.includes('Failed to fetch')) {
        setError('Payment history is only available for purchases made after 09/16/2025');
      } else {
        setError('Failed to load payment history. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateForDisplay(dateString, 'MMMM d, yyyy \'at\' h:mm a');
  };

  const formatPurchaseDate = (dateString) => {
    return formatDateForDisplay(dateString, 'MMMM d, yyyy');
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const filteredHistory = history.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      activeFilter === 'all' || 
      (activeFilter === 'courses' && item.type === 'course') ||
      (activeFilter === 'products' && item.type === 'product');
    
    return matchesSearch && matchesFilter;
  });

  const totalSpent = history.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const courseCount = history.filter(item => item.type === 'course').length;
  const productCount = history.filter(item => item.type === 'product').length;

  if (loading) {
    return (
      <PaymentHistoryContainer>
        <Header>
          <Title>Payment History</Title>
        </Header>
        <LoadingState>
          Loading payment history...
        </LoadingState>
      </PaymentHistoryContainer>
    );
  }

  return (
    <PaymentHistoryContainer>
      <Header>
        <div>
          <Title>Payment History</Title>
          <Subtitle>Review course enrollments, product purchases, receipts, and payment details.</Subtitle>
        </div>
        <SearchBar>
          <SearchIcon>
            <FiSearch />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search payment history..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
      </Header>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel><FiDollarSign /> Total Paid</SummaryLabel>
          <SummaryValue>{formatAmount(totalSpent)}</SummaryValue>
          <SummaryHint>Across {history.length} transaction{history.length === 1 ? '' : 's'}</SummaryHint>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel><FiBookOpen /> Course Enrollments</SummaryLabel>
          <SummaryValue>{courseCount}</SummaryValue>
          <SummaryHint>Tuition and course access purchases</SummaryHint>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel><FiShoppingBag /> Product Purchases</SummaryLabel>
          <SummaryValue>{productCount}</SummaryValue>
          <SummaryHint>Materials and other purchases</SummaryHint>
        </SummaryCard>
      </SummaryGrid>

      <Filters>
        <FilterButton 
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All Transactions
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'courses'}
          onClick={() => setActiveFilter('courses')}
        >
          Course Enrollments
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'products'}
          onClick={() => setActiveFilter('products')}
        >
          Product Purchases
        </FilterButton>
      </Filters>

      {error && (
        error.includes('only available') ? (
          <InfoMessage>
            {error}
          </InfoMessage>
        ) : (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )
      )}

      {!error && filteredHistory.length === 0 && (
        <EmptyState>
          <EmptyIcon>
            <FiCreditCard />
          </EmptyIcon>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontFamily: "'Inter', sans-serif", fontSize: '1.25rem', fontWeight: '600' }}>
            {searchTerm || activeFilter !== 'all' ? 'No matching transactions found' : 'No Payment History'}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            {searchTerm || activeFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Payment history is only available for purchases made after 09/16/2025.'
            }
          </p>
        </EmptyState>
      )}

      {!error && filteredHistory.length > 0 && (
        <HistoryList>
          {filteredHistory.map((item, index) => (
            <HistoryCard
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <CardHeader>
                <CardTitle>
                  <TypeIcon $type={item.type}>
                    {item.typeIcon}
                  </TypeIcon>
                  <HeaderMeta>
                    <TypeLabel>{item.typeLabel}</TypeLabel>
                    <CardDate>
                      <FiCalendar />
                      {formatDate(item.date)}
                    </CardDate>
                  </HeaderMeta>
                </CardTitle>
                <HeaderAmount>
                  <AmountLabel>Amount paid</AmountLabel>
                  <AmountSection>{formatAmount(item.amount)}</AmountSection>
                </HeaderAmount>
              </CardHeader>
              
              <CardContent>
                <ItemName>{item.name}</ItemName>
                <ItemDescription>
                  <DescriptionText>{item.description}</DescriptionText>
                  <StatusBadge status={item.status}>
                    <FiCheckCircle />
                    {item.status === 'completed' ? 'Completed' : 'Pending'}
                  </StatusBadge>
                </ItemDescription>

                <PaymentDetails>
                  <DetailItem>
                    <DetailLabel>Purchase Date</DetailLabel>
                    <DetailValue>{formatPurchaseDate(item.purchaseDate)}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Order Number</DetailLabel>
                    <DetailValue>{item.orderNumber}</DetailValue>
                  </DetailItem>
                  {item.receiptUrl && (
                    <DetailItem>
                      <DetailLabel>Receipt</DetailLabel>
                      <ReceiptLink href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                        View Receipt
                      </ReceiptLink>
                    </DetailItem>
                  )}
                  {item.type === 'course' && item.originalPrice > 0 && item.originalPrice !== item.amount && (
                    <DetailItem>
                      <DetailLabel>Original Price</DetailLabel>
                      <DetailValue>{formatAmount(item.originalPrice)}</DetailValue>
                    </DetailItem>
                  )}
                  {item.type === 'course' && item.originalPrice > 0 && item.originalPrice !== item.amount && (
                    <DetailItem>
                      <DetailLabel>Discount</DetailLabel>
                      <DiscountBadge>-{formatAmount(item.originalPrice - item.amount)}</DiscountBadge>
                    </DetailItem>
                  )}
                  {item.type === 'course' && item.paymentIntentId && (
                    <DetailItem>
                      <DetailLabel>Payment ID</DetailLabel>
                      <DetailValue style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {item.paymentIntentId}
                      </DetailValue>
                    </DetailItem>
                  )}
                </PaymentDetails>
                
              </CardContent>
            </HistoryCard>
          ))}
        </HistoryList>
      )}
    </PaymentHistoryContainer>
  );
}

export default PaymentHistoryPage;
